import { useState } from 'react';
import { useContactDetailsQuery } from '@data/settings/useContactDetailsQuery';

interface FooterContactUsProps {
  isMobile: boolean;
}

const MailIcon = () => (
  <svg className="footer__contact-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="footer__contact-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
  </svg>
);

const LocationIcon = () => (
  <svg className="footer__contact-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
  </svg>
);

interface ContactItem {
  key: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

function FooterContactUs({ isMobile }: FooterContactUsProps) {
  const { data, isLoading, error } = useContactDetailsQuery();
  const [copied, setCopied] = useState(false);

  if (isLoading || error || !data) {
    return null;
  }

  const { email, phone, address } = data;
  const addressText = typeof address === 'string' ? address : address?.formattedAddress;

  if (!email && !phone && !addressText) {
    return null;
  }

  const handleCopyAddress = () => {
    if (addressText) {
      navigator.clipboard.writeText(addressText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const items: ContactItem[] = [];

  if (email) {
    items.push({
      key: 'email',
      icon: <MailIcon />,
      content: (
        <a href={`mailto:${email}`} className="footer__contact-link">
          {email}
        </a>
      ),
    });
  }

  if (phone) {
    items.push({
      key: 'phone',
      icon: <PhoneIcon />,
      content: (
        <a href={`tel:${phone}`} className="footer__contact-link">
          {phone}
        </a>
      ),
    });
  }

  if (addressText) {
    items.push({
      key: 'address',
      icon: <LocationIcon />,
      content: (
        <button className="footer__contact-address" onClick={handleCopyAddress}>
          {copied ? 'Copied!' : addressText}
        </button>
      ),
    });
  }

  return (
    <div className={`footer__contact${isMobile ? ' footer__contact--mobile' : ''}`}>
      <h3 className="footer__section-title">Contact Us</h3>
      <div className="footer__contact-items">
        {items.map((item, index) => (
          <span key={item.key}>
            <span className="footer__contact-row">
              {item.icon}
              {item.content}
            </span>
            {!isMobile && index < items.length - 1 && <span className="footer__dot-separator text-m">&middot;</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

export default FooterContactUs;
